import React from 'react';
import { classNames, layoutClassNames, positionClassNames, surfaceClassNames } from '../atoms';
import type {
  LayoutClassNameProps,
  MarginSpace,
  PositionClassNameProps,
  SurfaceClassNameProps,
} from '../atoms';

export type FlexAlign = 'start' | 'end' | 'center' | 'baseline' | 'stretch';
export type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';
export type FlexGap = 0 | 1 | 2 | 3 | 4 | 5;
export type FlexJustify = 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly';
export type FlexWrap = boolean | 'reverse';
export type FlexBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type FlexDirectionAt = Partial<Record<FlexBreakpoint, FlexDirection>>;
export type FlexMarginBottomAt = Partial<Record<FlexBreakpoint, MarginSpace>>;

export interface FlexClassNameOptions
  extends LayoutClassNameProps,
    SurfaceClassNameProps,
    PositionClassNameProps {
  align?: FlexAlign;
  className?: string;
  direction?: FlexDirection;
  directionAt?: FlexDirectionAt;
  fullHeight?: boolean;
  fullWidth?: boolean;
  gap?: FlexGap;
  justify?: FlexJustify;
  marginBottomAt?: FlexMarginBottomAt;
  wrap?: FlexWrap;
}

export interface FlexProps extends React.HTMLAttributes<HTMLElement>, Omit<FlexClassNameOptions, 'className'> {
  as?: React.ElementType;
}

const getWrapClassName = (wrap: FlexWrap | undefined): string | undefined => {
  if (wrap === 'reverse') return 'flex-wrap-reverse';
  if (wrap) return 'flex-wrap';
  return undefined;
};

const getResponsiveMarginBottomClassNames = (
  marginBottomAt: FlexMarginBottomAt | undefined,
): Array<string | undefined> => (
  ['sm', 'md', 'lg', 'xl', 'xxl'] as const
).map((breakpoint) => (
  marginBottomAt?.[breakpoint] !== undefined ? `mb-${breakpoint}-${marginBottomAt[breakpoint]}` : undefined
));

export const getFlexClassName = ({
  align,
  className,
  direction = 'row',
  directionAt,
  fullHeight = false,
  fullWidth = false,
  gap,
  justify,
  margin,
  marginTop,
  marginEnd,
  marginBottom,
  marginBottomAt,
  marginStart,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingEnd,
  paddingBottom,
  paddingStart,
  paddingX,
  paddingY,
  textAlign,
  background,
  bordered,
  borderless,
  borderColor,
  rounded,
  shadow,
  bottom,
  end,
  fixed,
  position,
  positionTranslate,
  start,
  top,
  wrap,
}: FlexClassNameOptions): string => classNames(
  'd-flex',
  direction !== 'row' ? `flex-${direction}` : undefined,
  directionAt?.sm ? `flex-sm-${directionAt.sm}` : undefined,
  directionAt?.md ? `flex-md-${directionAt.md}` : undefined,
  directionAt?.lg ? `flex-lg-${directionAt.lg}` : undefined,
  directionAt?.xl ? `flex-xl-${directionAt.xl}` : undefined,
  directionAt?.xxl ? `flex-xxl-${directionAt.xxl}` : undefined,
  align ? `align-items-${align}` : undefined,
  justify ? `justify-content-${justify}` : undefined,
  getWrapClassName(wrap),
  gap !== undefined ? `gap-${gap}` : undefined,
  fullHeight ? 'h-100' : undefined,
  fullWidth ? 'w-100' : undefined,
  ...getResponsiveMarginBottomClassNames(marginBottomAt),
  positionClassNames({
    bottom,
    end,
    fixed,
    position,
    positionTranslate,
    start,
    top,
  }),
  surfaceClassNames({
    background,
    bordered,
    borderless,
    borderColor,
    rounded,
    shadow,
  }),
  layoutClassNames({
    margin,
    marginTop,
    marginEnd,
    marginBottom,
    marginStart,
    marginX,
    marginY,
    padding,
    paddingTop,
    paddingEnd,
    paddingBottom,
    paddingStart,
    paddingX,
    paddingY,
    textAlign,
  }),
  className,
);

const Flex: React.FC<FlexProps> = ({
  align,
  as: Component = 'div',
  className,
  children,
  direction = 'row',
  directionAt,
  fullHeight = false,
  fullWidth = false,
  gap,
  justify,
  margin,
  marginTop,
  marginEnd,
  marginBottom,
  marginBottomAt,
  marginStart,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingEnd,
  paddingBottom,
  paddingStart,
  paddingX,
  paddingY,
  textAlign,
  background,
  bordered,
  borderless,
  borderColor,
  rounded,
  shadow,
  bottom,
  end,
  fixed,
  position,
  positionTranslate,
  start,
  top,
  wrap,
  ...props
}) => (
  <Component
    className={getFlexClassName({
      align,
      className,
      direction,
      directionAt,
      fullHeight,
      fullWidth,
      gap,
      justify,
      margin,
      marginTop,
      marginEnd,
      marginBottom,
      marginBottomAt,
      marginStart,
      marginX,
      marginY,
      padding,
      paddingTop,
      paddingEnd,
      paddingBottom,
      paddingStart,
      paddingX,
      paddingY,
      textAlign,
      background,
      bordered,
      borderless,
      borderColor,
      rounded,
      shadow,
      bottom,
      end,
      fixed,
      position,
      positionTranslate,
      start,
      top,
      wrap,
    })}
    {...props}
  >
    {children}
  </Component>
);

export default Flex;
